import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Refresh from '@mui/icons-material/Refresh';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Warning from '@mui/icons-material/Warning';
import Error from '@mui/icons-material/Error';
import Speed from '@mui/icons-material/Speed';
import Timer from '@mui/icons-material/Timer';
import {
  systemHealthService,
  SystemHealthDashboard,
  RateLimiterSnapshot,
  ActionRecommendation,
} from '../../services/systemHealthService';

interface SystemHealthPanelProps {
  onNavigateToSettings?: () => void;
}

const REFRESH_INTERVAL_MS = 30_000;

/** Map utilization % to MUI color for LinearProgress */
const utilizationColor = (pct: number): 'success' | 'warning' | 'error' =>
  pct > 80 ? 'error' : pct > 60 ? 'warning' : 'success';

const severityMap: Record<string, 'success' | 'warning' | 'error'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
};

const severityIcon: Record<string, React.ReactNode> = {
  healthy: <CheckCircle />,
  degraded: <Warning />,
  unhealthy: <Error />,
};

const queueChipColor = (depth: number): 'success' | 'warning' | 'error' =>
  depth > 50 ? 'error' : depth > 20 ? 'warning' : 'success';

export const SystemHealthPanel: React.FC<SystemHealthPanelProps> = ({ onNavigateToSettings }) => {
  const [dashboard, setDashboard] = useState<SystemHealthDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadDashboard = useCallback(async () => {
    try {
      const data = await systemHealthService.getDashboard();
      setDashboard(data);
      setLastUpdated(new Date());
      setError(null);
    } catch {
      setError('Failed to load system health data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !dashboard) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!dashboard) return null;

  const { health, metrics, analysisRateLimiter, summaryRateLimiter, speechRateLimiter, tenantFailures, recommendations } = dashboard;
  const secondsAgo = lastUpdated ? Math.round((Date.now() - lastUpdated.getTime()) / 1000) : 0;

  return (
    <Box>
      {/* Health Banner */}
      <Alert
        severity={severityMap[health.status] ?? 'info'}
        icon={severityIcon[health.status]}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {secondsAgo}s ago
            </Typography>
            <Tooltip title="Refresh now">
              <IconButton size="small" onClick={loadDashboard}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
          {health.status}
        </Typography>
        <Typography variant="body2">
          {health.reason} — Failure rate: {health.failureRatePercent}%
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Window start: {new Date(metrics.windowStart).toLocaleTimeString()} · Auto-refreshes every 30s
        </Typography>
      </Alert>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {recommendations.map((rec, i) => (
            <RecommendationAlert key={i} rec={rec} onNavigateToSettings={onNavigateToSettings} />
          ))}
        </Box>
      )}

      {/* Three Service Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <AiServiceCard title="AI Analysis" subtitle="gpt-oss-120b" limiter={analysisRateLimiter} model={metrics.analysis} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <AiServiceCard title="AI Summary" subtitle="gpt-5-nano" limiter={summaryRateLimiter} model={metrics.summary} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <SpeechServiceCard speech={speechRateLimiter} />
        </Grid>
      </Grid>

      {/* Queue Depths */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          <Speed fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Queue Depths
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Analysis Queue: ${metrics.currentQueueDepth}`} color={queueChipColor(metrics.currentQueueDepth)} variant="outlined" />
          <Chip label={`Retry Queue: ${metrics.retryQueueDepth}`} color={queueChipColor(metrics.retryQueueDepth)} variant="outlined" />
          <Chip label={`Permanent Failures: ${metrics.permanentFailures}`} color={metrics.permanentFailures > 0 ? 'error' : 'success'} variant="outlined" />
        </Box>
      </Paper>

      {/* Tenant Failures Table */}
      {Object.keys(tenantFailures).length > 0 && (
        <TenantFailuresTable tenantFailures={tenantFailures} />
      )}
    </Box>
  );
};

/* ─── Sub-components ─── */

const RecommendationAlert: React.FC<{ rec: ActionRecommendation; onNavigateToSettings?: () => void }> = ({ rec, onNavigateToSettings }) => {
  const severity = rec.severity === 'critical' ? 'error' : rec.severity === 'warning' ? 'warning' : 'info';
  return (
    <Alert severity={severity} sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip label={rec.category} size="small" variant="outlined" />
        <Typography variant="body2">{rec.message}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {rec.action}
        {rec.settingKey && onNavigateToSettings && (
          <Typography
            component="span"
            variant="body2"
            sx={{ ml: 1, cursor: 'pointer', textDecoration: 'underline' }}
            color="primary"
            onClick={onNavigateToSettings}
          >
            Go to Settings
          </Typography>
        )}
      </Typography>
    </Alert>
  );
};

interface AiServiceCardProps {
  title: string;
  subtitle: string;
  limiter: RateLimiterSnapshot;
  model: { requests: number; tokensConsumed: number; avgResponseTimeMs: number; p95ResponseTimeMs: number; failures: number };
}

const AiServiceCard: React.FC<AiServiceCardProps> = ({ title, subtitle, limiter, model }) => {
  const successful = model.requests - model.failures;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>{title}</Typography>
        <Typography variant="caption" color="text.secondary" gutterBottom>{subtitle}</Typography>

        <Box sx={{ mt: 1, mb: 0.5 }}>
          <Typography variant="body2">
            Requests:{' '}
            <Typography component="span" variant="body2" color="success.main">{successful} ✓</Typography>
            {' / '}
            <Typography component="span" variant="body2" color="error.main">{model.failures} ✗</Typography>
          </Typography>
        </Box>

        <UtilizationBar label="RPM" used={limiter.requestsInLastMinute} max={limiter.maxRequestsPerMinute} pct={limiter.rpmUtilizationPercent} />
        <UtilizationBar label="TPM" used={limiter.tokensConsumedInLastMinute} max={limiter.maxTokensPerMinute} pct={limiter.tpmUtilizationPercent} />

        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Concurrent: {limiter.concurrentSlotsUsed}/{limiter.concurrentSlotsMax} slots
        </Typography>
        <Typography variant="body2">
          <Timer fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Avg: {model.avgResponseTimeMs.toFixed(0)}ms · P95: {model.p95ResponseTimeMs.toFixed(0)}ms
        </Typography>
      </CardContent>
    </Card>
  );
};

const SpeechServiceCard: React.FC<{ speech: { requestsInWindow: number; maxRequestsPerWindow: number; utilizationPercent: number; isPaused: boolean; pausedUntilUtc: string | null } }> = ({ speech }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="subtitle1" gutterBottom>Transcription</Typography>
      <Typography variant="caption" color="text.secondary" gutterBottom>Azure Speech Batch API</Typography>

      <Box sx={{ mt: 1 }}>
        <UtilizationBar label="Requests (10s)" used={speech.requestsInWindow} max={speech.maxRequestsPerWindow} pct={speech.utilizationPercent} />
      </Box>

      <Box sx={{ mt: 1 }}>
        {speech.isPaused ? (
          <Chip
            label={`Paused until ${speech.pausedUntilUtc ? new Date(speech.pausedUntilUtc).toLocaleTimeString() : '...'}`}
            color="error"
            size="small"
          />
        ) : (
          <Chip label="Active" color="success" size="small" />
        )}
      </Box>
    </CardContent>
  </Card>
);

const UtilizationBar: React.FC<{ label: string; used: number; max: number; pct: number }> = ({ label, used, max, pct }) => (
  <Box sx={{ mb: 0.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="caption">{label}</Typography>
      <Typography variant="caption">{used.toLocaleString()}/{max.toLocaleString()} ({pct}%)</Typography>
    </Box>
    <LinearProgress variant="determinate" value={Math.min(pct, 100)} color={utilizationColor(pct)} sx={{ height: 6, borderRadius: 1 }} />
  </Box>
);

const TenantFailuresTable: React.FC<{ tenantFailures: Record<string, number> }> = ({ tenantFailures }) => {
  const total = Object.values(tenantFailures).reduce((a, b) => a + b, 0);
  const sorted = Object.entries(tenantFailures).sort(([, a], [, b]) => b - a);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Tenant Failures</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tenant ID</TableCell>
            <TableCell align="right">Failures</TableCell>
            <TableCell align="right">% of Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(([tenantId, count]) => (
            <TableRow key={tenantId}>
              <TableCell>{tenantId}</TableCell>
              <TableCell align="right">{count}</TableCell>
              <TableCell align="right">{total > 0 ? ((count / total) * 100).toFixed(0) : 0}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};
